//TODO THIS IS FOR TESTING PURPOSES ONLY AND WILL BE REMOVED AFTER THE QA PROCESS IS COMPLETED

import { Injectable } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import { 
  BoxplotChartConfig, 
  MixedChartConfig, 
  TimeSeriesChartConfig,
  LineChartConfig,
  BarChartConfig,
  PieChartConfig
} from '../models/chart-types';

/**
 * Mock API Service for Chart Data
 * Simulates backend API endpoints with realistic data
 */
@Injectable({
  providedIn: 'root',
})
export class MockChartApiService {
  /**
   * Get risk distribution data (Boxplot with scatter overlay)
   */
  getRiskDistribution(): Observable<BoxplotChartConfig> {
    const config: BoxplotChartConfig = {
      type: 'boxplot',
      title: 'Risk Score Distribution',
      subtitle: 'Employee vs Peer Group Analysis',
      theme: 'dark',
      
      xAxis: {
        type: 'category',
        name: 'Risk Categories',
      },
      
      yAxis: {
        type: 'value',
        name: 'Risk Score',
        axisLabel: {
          formatter: '{value}',
        },
      },
      
      categories: [
        'Operational',
        'Financial',
        'Compliance',
        'Strategic',
        'Reputational',
      ],
      
      boxplotSeries: [
        {
          name: 'Peer Group',
          data: [
            [45, 55, 65, 75, 85],
            [40, 50, 60, 70, 80],
            [35, 45, 55, 65, 75],
            [50, 60, 70, 80, 90],
            [30, 40, 50, 60, 70],
          ],
          color: '#3b82f6',
        },
      ],
      
      scatterSeries: [
        {
          name: 'Your Score',
          data: [
            [0, 82],
            [1, 68],
            [2, 48],
            [3, 92],
            [4, 55],
          ],
          color: '#ef4444',
          symbolSize: 14,
          itemStyle: {
            borderWidth: 2,
            borderColor: '#fff',
          },
        },
      ],
      
      tooltip: {
        trigger: 'item',
      },
    };

    return of(config).pipe(delay(500));
  }

  /**
   * Get performance distribution (Mixed bar + line chart)
   */
  getPerformanceDistribution(): Observable<MixedChartConfig> {
    const config: MixedChartConfig = {
      type: 'mixed',
      title: 'Performance Score Distribution',
      subtitle: 'Employee vs Peer Comparison',
      theme: 'dark',
      
      xAxis: {
        type: 'category',
        data: ['0-20', '21-40', '41-60', '61-80', '81-100'],
        name: 'Score Range',
      },
      
      yAxis: {
        type: 'value',
        name: 'Employee Count',
      },
      
      barSeries: [
        {
          name: 'Distribution',
          data: [8, 23, 48, 65, 42],
          color: '#3b82f6',
          barWidth: '60%',
        },
      ],
      
      lineSeries: [
        {
          name: 'Your Score',
          data: [5, 18, 45, 75, 85] as any,
          color: '#ef4444',
          smooth: true,
          showSymbol: true,
          lineStyle: {
            width: 3,
          },
        },
        {
          name: 'Team Average',
          data: [6, 20, 62, 68, 72] as any,
          color: '#10b981',
          smooth: true,
          showSymbol: true,
          lineStyle: {
            width: 3,
          },
        },
      ],
      
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
    };

    return of(config).pipe(delay(500));
  }

  /**
   * Get risk trends over time (Time series)
   */
  getRiskTrends(): Observable<TimeSeriesChartConfig> {
    const config: TimeSeriesChartConfig = {
      type: 'timeseries',
      title: 'Risk Trends Over Time',
      subtitle: 'Last 12 Months',
      theme: 'dark',
      
      xAxis: {
        type: 'category',
        data: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        boundaryGap: false,
      },
      
      yAxis: {
        type: 'value',
        name: 'Risk Score',
      },
      
      series: [
        {
          name: 'Operational Risk',
          data: [65, 68, 62, 70, 72, 75, 73, 71, 69, 74, 76, 78],
          smooth: true,
          showSymbol: true,
          color: '#3b82f6',
          lineStyle: {
            width: 3,
          },
        },
        {
          name: 'Financial Risk',
          data: [55, 58, 60, 62, 65, 63, 68, 70, 72, 75, 73, 71],
          smooth: true,
          showSymbol: true,
          color: '#8b5cf6',
          lineStyle: {
            width: 3,
          },
        },
        {
          name: 'Compliance Risk',
          data: [45, 48, 50, 52, 49, 51, 53, 55, 58, 60, 62, 64],
          smooth: true,
          showSymbol: true,
          color: '#10b981',
          lineStyle: {
            width: 3,
          },
        },
        {
          name: 'Strategic Risk',
          data: [75, 78, 80, 77, 79, 82, 85, 83, 81, 84, 86, 88],
          smooth: true,
          showSymbol: true,
          color: '#f59e0b',
          lineStyle: {
            width: 3,
          },
        },
      ],
      
      tooltip: {
        trigger: 'axis',
      },
    };

    return of(config).pipe(delay(500));
  }

  /**
   * Get simple line chart data
   */
  getLineChartData(): Observable<LineChartConfig> {
    const config: LineChartConfig = {
      type: 'line',
      title: 'Monthly Revenue',
      subtitle: '2024',
      theme: 'dark',
      
      xAxis: {
        type: 'category',
        data: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        boundaryGap: false,
      },
      
      yAxis: {
        type: 'value',
        name: 'Revenue ($K)',
      },
      
      series: [
        {
          name: 'Revenue',
          data: [120, 132, 101, 134, 90, 230],
          smooth: true,
          color: '#3b82f6',
          areaStyle: {
            color: 'rgba(59, 130, 246, 0.1)',
          },
        },
      ],
      
      tooltip: {
        trigger: 'axis',
      },
    };

    return of(config).pipe(delay(500));
  }

  /**
   * Get bar chart data
   */
  getBarChartData(): Observable<BarChartConfig> {
    const config: BarChartConfig = {
      type: 'bar',
      title: 'Department Performance',
      subtitle: 'Q4 2024',
      theme: 'dark',
      
      xAxis: {
        type: 'category',
        data: ['Sales', 'Marketing', 'Engineering', 'HR', 'Finance'],
      },
      
      yAxis: {
        type: 'value',
        name: 'Score',
      },
      
      series: [
        {
          name: 'Performance Score',
          data: [85, 72, 93, 68, 78],
          color: '#8b5cf6',
        },
      ],
      
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
    };

    return of(config).pipe(delay(500));
  }

  /**
   * Get pie chart data
   */
  getPieChartData(): Observable<PieChartConfig> {
    const config: PieChartConfig = {
      type: 'pie',
      title: 'Market Share',
      subtitle: '2024',
      theme: 'dark',
      
      series: [
        {
          name: 'Market Share',
          data: [
            { name: 'Product A', value: 335 },
            { name: 'Product B', value: 234 },
            { name: 'Product C', value: 548 },
            { name: 'Product D', value: 135 },
            { name: 'Product E', value: 147 },
          ],
          radius: ['40%', '70%'],
          center: ['50%', '55%'],
        },
      ],
      
      legend: {
        show: true,
        orient: 'vertical',
        left: 'left',
        top: 80,
      },
      
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)',
      },
    };

    return of(config).pipe(delay(500));
  }
}

